import { zodResolver } from '@hookform/resolvers/zod';
import { CancelReason, DimensionUnit, FreightService, WeightUnit } from '@pelicargo/types';
import { prettyDate, QuoteStatusConfirmedLike } from '@pelicargo/utils';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EmailTemplateName } from '../../../../api/src/lib/codegen/email';
import { useAuth } from '../../context/AuthProvider';
import {
  AirlineFormValue,
  airlineValidationSchema,
  ensureFreightServiceIs,
  ensureQuoteStatusIs,
  getDefaultAirlinesFormFromQuote,
  getDefaultCustomMessage,
  getDefaultTruckingsFormFromQuote,
  getDefaultWarehousesFormFromQuote,
  getOpenerByEmailTemplate,
  getSharedAirlineDefaultsFromRequest,
  getSharedInlandDefaultsFromRequest,
  SharedEmailOverrides,
  truckingValidationSchema,
  warehouseValidationSchema,
} from '../../utils/email';
import { Quote, Request } from '../../utils/types';

export enum BookingType {
  'Request',
  'Change',
  'Cancel',
  'Schedule',
}

export type AirlineBookingValues = SharedEmailOverrides & {
  cancelReason?: CancelReason;
  isLongEmail: boolean;
  providers: AirlineFormValue[];
};

const validationSchema = z.object({
  senderContactId: z.coerce.number().min(1, 'You must specify a sender contact'),
  isLongEmail: z.boolean(),
  cancelReason: z.nativeEnum(CancelReason).nullish(),
  clipboard: z.boolean().optional().default(false),
  contentOnly: z.boolean().optional().default(false),

  origin: z.string(),
  destination: z.string(),
  weightUnit: z.nativeEnum(WeightUnit),
  dimensionUnit: z.nativeEnum(DimensionUnit),

  customMessage: z.string().optional(),
});

const sectionSchema = z
  .object({
    airlines: validationSchema.extend({ providers: z.array(airlineValidationSchema).optional() }),
    trucks: validationSchema.extend({ providers: z.array(truckingValidationSchema).optional() }),
    warehouses: validationSchema.extend({ providers: z.array(warehouseValidationSchema).optional() }),
  })
  .superRefine((data, ctx) => {
    const providerTypes = ['airlines', 'trucks', 'warehouses'] as const;
    const hasAtLeastOneProvider = providerTypes.some((type) => data?.[type]?.providers?.length > 0);
    if (!hasAtLeastOneProvider) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must add at least one provider',
      });
    }

    const hasAtLeastOneRecipient = providerTypes.some((type) =>
      data?.[type]?.providers?.some((provider) => provider?.recipients?.length > 0),
    );
    if (!hasAtLeastOneRecipient) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must add at least one recipient',
      });
    }
  });

export type AirlineBookingSection = z.infer<typeof sectionSchema>;

const filterQuotes = (quotes?: Quote[], bookingType?: BookingType) => {
  if (bookingType === BookingType.Schedule) {
    // one per airline
    return quotes
      .reduce(
        (agg, quote) => {
          if (!agg.handled.has(quote.provider_id)) {
            agg.handled.add(quote.provider_id);
            agg.quotes.push(quote);
          }
          return agg;
        },
        {
          quotes: [] as typeof quotes,
          handled: new Set<string>(),
        },
      )
      .quotes?.filter(ensureFreightServiceIs([FreightService.AFR]));
  } else {
    return quotes?.filter(ensureQuoteStatusIs(QuoteStatusConfirmedLike));
  }
};

export const useAirlineBookingForm = (request?: Request, quotes?: Quote[], bookingType?: BookingType) => {
  const { user } = useAuth();

  const customMessage = useMemo(() => {
    const prettyTenderDate = prettyDate(request?.booked_quote_variant?.quote?.tender_date, { timeZone: 'Etc/UTC' });
    switch (bookingType) {
      case BookingType.Request:
        return getOpenerByEmailTemplate(EmailTemplateName.AIRLINE_BOOKING);
      case BookingType.Change:
        return getDefaultCustomMessage(
          `Can you please rebook this AWB as per below?\nAWB#: {{awb_number}}\nNew Tender Date: ${prettyTenderDate}`,
        );
      case BookingType.Cancel:
        return getDefaultCustomMessage(`\n\nCan you please cancel {{awb_number}}?`);
      case BookingType.Schedule:
        return getDefaultCustomMessage(
          `\n\nCan you please provide an estimated schedule for the below freight if tendered on {{drop_off_date}}?`,
        );
      default:
        return '';
    }
  }, [bookingType, request?.booked_quote_variant?.quote?.tender_date]);

  const possibleQuotes = useMemo(() => filterQuotes(quotes, bookingType), [quotes, bookingType]);

  const defaultAirlines = useMemo(() => {
    return possibleQuotes?.filter(ensureFreightServiceIs([FreightService.AFR]))?.map((quote) => ({
      ...getDefaultAirlinesFormFromQuote(quote),
      isExpanded: false,
    }));
  }, [possibleQuotes]);

  const defaultTrucks = useMemo(() => {
    return possibleQuotes?.filter(ensureFreightServiceIs([FreightService.EXW]))?.map((quote) => ({
      ...getDefaultTruckingsFormFromQuote(quote),
      isExpanded: false,
    }));
  }, [possibleQuotes]);

  const defaultWarehouses = useMemo(() => {
    return possibleQuotes?.filter(ensureFreightServiceIs([FreightService.FCA]))?.map((quote) => ({
      ...getDefaultWarehousesFormFromQuote(quote),
      isExpanded: false,
    }));
  }, [possibleQuotes]);

  const defaultValues = useMemo(() => {
    const sharedAirlineDefaults = getSharedAirlineDefaultsFromRequest(request);
    const sharedInlandDefaults = getSharedInlandDefaultsFromRequest(request);
    const defaultSenderContactId = user?.contact_id && user?.contact_id > 0 ? user.contact_id : undefined;

    return {
      airlines: {
        ...sharedAirlineDefaults,
        customMessage,
        isLongEmail: false,
        senderContactId: defaultSenderContactId,
        providers: defaultAirlines,
      },
      trucks: {
        ...sharedInlandDefaults,
        origin: request?.cargo_origin?.zip_code || '',
        destination: sharedInlandDefaults?.origin || '',
        customMessage,
        isLongEmail: false,
        senderContactId: defaultSenderContactId,
        providers: defaultTrucks,
      },
      warehouses: {
        ...sharedInlandDefaults,
        destination: sharedInlandDefaults?.origin || '',
        customMessage,
        isLongEmail: false,
        senderContactId: defaultSenderContactId,
        providers: defaultWarehouses,
      },
    };
  }, [request, customMessage, defaultAirlines, defaultTrucks, defaultWarehouses, user?.contact_id]);

  const methods = useForm<Partial<AirlineBookingSection>>({
    mode: 'onSubmit',
    resolver: zodResolver(sectionSchema),
    defaultValues,
  });

  return { methods };
};
